import { AlertTriangle, BookOpen, Lightbulb, Star, type LucideIcon } from 'lucide-react';

import { CALLOUT_VARIANT_LABELS, CALLOUT_VARIANT_STYLE } from '@/lib/callout';
import type { CalloutContent, CalloutVariant, ScriptElement } from '@/lib/schema.types';

const VARIANT_GLYPH: Record<CalloutVariant, LucideIcon> = {
  common_mistake: AlertTriangle,
  memory_trick: Lightbulb,
  important_concept: Star,
  concept: BookOpen,
};

/**
 * A styled callout box. Variant decides the colour chrome + leading glyph; the
 * classroom mirrors this rendering (parity spec §13).
 */
export function CalloutElement({ element }: { element: ScriptElement }) {
  const c = element.content as CalloutContent | undefined;
  const variant: CalloutVariant = c?.variant ?? 'important_concept';
  const theme = CALLOUT_VARIANT_STYLE[variant];
  const Glyph = VARIANT_GLYPH[variant];
  const title = c?.title?.trim() || CALLOUT_VARIANT_LABELS[variant];
  // Authored text size (matches the player's default of 20 so edit == play).
  const bodySize = (element.style?.fontSize as number | undefined) ?? 20;
  const cardShadow = c?.shadow ? '0 10px 24px rgba(15, 23, 42, 0.18)' : undefined;

  // `concept` — minimal definition card (mirrors the player): left bar in the
  // authored accent color, bold neutral title, regular body. No icon.
  if (variant === 'concept') {
    return (
      <div
        className="w-full rounded-[10px] border p-3"
        style={{
          background: theme.bg,
          borderColor: theme.border,
          borderLeft: `5px solid ${c?.accentColor ?? theme.accent}`,
          boxShadow: cardShadow,
        }}
      >
        <div
          className="font-bold"
          style={{ color: '#1e293b', fontSize: Math.round(bodySize * 0.9) }}
        >
          {title}
        </div>
        <div
          className="whitespace-pre-wrap font-normal"
          style={{
            color: (element.style?.color as string | undefined) ?? '#334155',
            fontSize: bodySize,
            lineHeight: 1.45,
          }}
        >
          {c?.body || 'Tap to edit this callout.'}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex w-full gap-2 rounded-lg border p-3"
      style={{ background: theme.bg, borderColor: theme.border, boxShadow: cardShadow }}
    >
      <Glyph size={18} color={theme.accent} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div
          className="font-semibold"
          style={{ color: theme.accent, fontSize: Math.max(11, Math.round(bodySize * 0.7)) }}
        >
          {title}
        </div>
        <div
          className="whitespace-pre-wrap"
          style={{
            color: (element.style?.color as string | undefined) ?? '#1f2937',
            fontSize: bodySize,
            lineHeight: 1.4,
          }}
        >
          {c?.body || 'Tap to edit this callout.'}
        </div>
      </div>
    </div>
  );
}
